import { Toast } from "@/components/ui/toast";
import { i18n } from "@/utils/i18n";

/**
 * API 错误类
 */
export class APIError extends Error {
  constructor(
    public message: string,
    public status?: number,
    /** 标记底层是否已调用过 Toast.error，上层可据此决定是否补充提示 */
    public toastShown = false,
  ) {
    super(message);
    this.name = "APIError";
  }
}

/**
 * 请求配置选项
 */
interface RequestOptions extends RequestInit {
  params?: Record<string, string | number>;
}

/**
 * 封装的 fetch 请求工具，统一处理：
 * 1. 默认 Headers (JSON)
 * 2. 401 身份失效 (弹出登录框)
 * 3. 业务错误 (解析 JSON 并提示 Toast)
 * 4. 网络错误 (提示 Toast)
 */
export async function request<T>(
  url: string,
  options: RequestOptions = {},
): Promise<T> {
  const { params, headers, ...rest } = options;

  // 处理 URL 查询参数
  let finalUrl = url;
  if (params) {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      searchParams.append(key, String(value));
    });
    const connector = url.includes("?") ? "&" : "?";
    finalUrl += `${connector}${searchParams.toString()}`;
  }

  // 默认请求头
  const defaultHeaders: HeadersInit = {
    "Content-Type": "application/json",
  };

  try {
    const response = await fetch(finalUrl, {
      ...rest,
      headers: {
        ...defaultHeaders,
        ...headers,
      },
    });

    // 1. 处理 401 身份失效
    if (response.status === 401) {
      // 抛出错误以防止后续逻辑继续执行
      throw new APIError(i18n.t("common.error.unauthorized"), 401);
    }

    // 2. 解析响应内容
    const isJson = response.headers
      .get("content-type")
      ?.includes("application/json");
    const data = isJson ? await response.json().catch(() => ({})) : {};

    // 3. 处理业务错误
    if (!response.ok) {
      const message = data.message || i18n.t("common.error.request_failed");
      Toast.error(message);
      throw new APIError(message, response.status, true);
    }

    return data as T;
  } catch (error) {
    // 如果是已经处理过的 APIError，直接向上抛
    if (error instanceof APIError) {
      throw error;
    }
    // 4. 网络错误捕获
    Toast.error(i18n.t("common.error.network"));
    throw error;
  }
}
